/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { toast } from "sonner";
import { Image as ImageIcon, FloppyDisk, ArrowCounterClockwise, ArrowSquareOut } from "@phosphor-icons/react";
import { api } from "../../lib/api";

const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
  return (typeof d === "string" && d) || e?.message || "Error";
};

const LBL = "block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5";
const SEL = "h-10 w-full px-3 border border-[#D9D5C6] rounded-md text-sm bg-white";

function Picker({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className={LBL}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={SEL}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function SlotThumb({ sec, sl, active, onPick }) {
  return (
    <button onClick={onPick} data-testid={`pi-thumb-${sec.key}-${sl.key}`}
            className={`text-left rounded-xl border overflow-hidden bg-white transition-[border-color,box-shadow] ${active ? "border-[#D23247] shadow-md" : "border-[#F0EEEA] hover:border-[#F5B5BD]"}`}>
      <div className="aspect-[4/3] bg-[#F7F6F2]">
        <img src={sl.url} alt="" className="w-full h-full object-cover" loading="lazy"/>
      </div>
      <div className="p-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">{sec.label}</div>
        <div className="text-xs font-medium text-slate-800 truncate">{sl.label}</div>
        {sl.overridden && <div className="text-[10px] text-[#D23247] mt-0.5">Custom</div>}
      </div>
    </button>
  );
}

export default function PageImagesCms() {
  const [pages, setPages] = useState([]);
  const [sel, setSel] = useState({ page: "", section: "", slot: "" });
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/page-images");
      setPages(r.data.pages);
      if (!sel.page && r.data.pages[0]) {
        const p = r.data.pages[0];
        setSel({ page: p.slug, section: p.sections[0].key, slot: p.sections[0].slots[0].key });
      }
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const page = pages.find(p => p.slug === sel.page);
  const section = page?.sections.find(s => s.key === sel.section);
  const slot = section?.slots.find(s => s.key === sel.slot);
  useEffect(() => { setUrl(slot?.url || ""); }, [slot?.url, sel.page, sel.section, sel.slot]);

  const pickPage = (slug) => {
    const p = pages.find(x => x.slug === slug);
    setSel({ page: slug, section: p.sections[0].key, slot: p.sections[0].slots[0].key });
  };
  const pickSection = (key) => {
    const s = page.sections.find(x => x.key === key);
    setSel(v => ({ ...v, section: key, slot: s.slots[0].key }));
  };

  const save = async (value) => {
    setSaving(true);
    try {
      await api.put("/admin/page-images", { ...sel, url: value });
      toast.success(value ? "Image updated" : "Reset to default image");
      await load();
    } catch (e) { toast.error(fmtErr(e)); }
    setSaving(false);
  };

  const pageOpts = useMemo(() => pages.map(p => ({ value: p.slug, label: p.label })), [pages]);
  if (!slot) return <AppShell><div className="p-8 text-sm text-slate-500">Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="p-6 md:p-8 space-y-6" data-testid="page-images-cms">
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">Content Management</div>
            <h1 className="font-heading text-3xl mt-1 text-slate-900">Page Images</h1>
            <p className="text-sm text-slate-600 mt-1">Pick a page, section and image location, then paste a new image URL.</p>
          </div>
          <a href={page.path} target="_blank" rel="noreferrer" data-testid="pi-preview"
             className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 bg-white text-sm">
            Open {page.path} <ArrowSquareOut size={12} weight="bold"/>
          </a>
        </header>

        <section className="bg-white rounded-2xl border border-[#F0EEEA] p-5 sm:p-6 grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <Picker label="Page" value={sel.page} onChange={pickPage} options={pageOpts} testid="pi-select-page"/>
              <Picker label="Section" value={sel.section} onChange={pickSection} testid="pi-select-section"
                      options={page.sections.map(s => ({ value: s.key, label: s.label }))}/>
              <Picker label="Image location" value={sel.slot} testid="pi-select-slot"
                      onChange={(k) => setSel(v => ({ ...v, slot: k }))}
                      options={section.slots.map(s => ({ value: s.key, label: s.label }))}/>
            </div>
            <div>
              <label className={LBL}>Image URL</label>
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…"
                     data-testid="pi-input-url"
                     className="h-10 w-full px-3 border border-[#D9D5C6] rounded-md text-sm font-mono"/>
              {slot.hint && <div className="text-[11px] text-slate-500 mt-1">Tip: {slot.hint}</div>}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => save(url.trim())} disabled={saving || !url.trim()} data-testid="pi-save"
                      className="inline-flex items-center gap-2 h-10 px-5 rounded-md text-white text-sm font-medium bg-[#D23247] hover:bg-[#B52236] disabled:opacity-60">
                <FloppyDisk size={14}/> {saving ? "Saving…" : "Save image"}
              </button>
              <button onClick={() => save("")} disabled={saving || !slot.overridden} data-testid="pi-reset"
                      className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 bg-white text-sm disabled:opacity-50">
                <ArrowCounterClockwise size={14}/> Reset to default
              </button>
            </div>
          </div>
          <div>
            <label className={LBL}>Live preview</label>
            <div className="aspect-[4/3] rounded-xl bg-[#F7F6F2] border border-[#F0EEEA] overflow-hidden grid place-items-center">
              {url ? <img src={url} alt="" data-testid="pi-preview-img" className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.opacity = 0.25; }}/>
                   : <ImageIcon size={28} className="text-slate-300"/>}
            </div>
            <div className="text-[11px] mt-1.5 text-slate-500" data-testid="pi-status">
              {slot.overridden ? "Using custom image" : "Using default image"}
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-lg text-slate-900">All images on {page.path}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {page.sections.flatMap(sec => sec.slots.map(sl => (
              <SlotThumb key={`${sec.key}.${sl.key}`} sec={sec} sl={sl}
                         active={sec.key === sel.section && sl.key === sel.slot}
                         onPick={() => setSel(v => ({ ...v, section: sec.key, slot: sl.key }))}/>
            )))}
          </div>
        </section>
      </div>
    </AppShell>
  );
}
